import { getApiInstance } from './api';
import { getCachedAthleteZones, invalidateAthleteZones } from './athleteZonesCache';

/** Full athlete record from GET /user/get-athletes or /account/athlete-details */
export interface AthleteDetail {
  id: number;
  first_name?: string;
  last_name?: string;
  full_name: string;
  profile_image?: string | null;
  mobile?: string;
  email?: string;
  birthday?: string | null;
  gender?: number | null;
  setting?: Record<string, unknown> | null;
  /** Effective weight: latest calendar metric up to today, else setting.weight (TrainingPeaks parity). */
  latest_weight?: number | string | null;
  metrics?: number[];
  connected_apps?: Record<string, boolean>;
}

export interface MetricOption {
  id: number;
  name: string;
  persian_name?: string | null;
}

export interface ZoneMethod {
  id: number;
  name: string;
  groupName?: string;
}

export const CLOUD_INTEGRATION_PROVIDERS = ['wahoo', 'whoop', 'coros', 'suunto', 'polar', 'strava'] as const;
export type CloudIntegrationProvider = (typeof CLOUD_INTEGRATION_PROVIDERS)[number];

export interface CloudIntegrationStatus {
  connected: boolean;
  provider_user_id?: string | null;
  scopes?: string[];
  expires_at?: string | null;
  last_synced_at?: string | null;
  last_error?: string | null;
}

const api = () => getApiInstance();

export const athleteSettingsApi = {
  /** GET /api/v1/mobile/user/get-athletes */
  getAthletes: () =>
    api().get<{ data: { athletes: AthleteDetail[] } }>('/api/v1/mobile/user/get-athletes'),

  /** GET /api/v1/mobile/account/athlete-details?id= */
  getAthleteDetails: (athleteId: number) =>
    api().get<{ data: { details: AthleteDetail } }>('/api/v1/mobile/account/athlete-details', {
      params: { id: athleteId },
    }),

  /** PATCH /api/v1/mobile/user/{userId} */
  updateProfile: (userId: number, data: Record<string, unknown>) =>
    api().patch(`/api/v1/mobile/user/${userId}`, data),

  /** PATCH /api/v1/mobile/setting/user/{userId} */
  updateSetting: (userId: number, data: Record<string, unknown>) =>
    api().patch(`/api/v1/mobile/setting/user/${userId}`, data).then(response => { invalidateAthleteZones(userId); return response; }),

  /** GET /api/v1/mobile/metric/ */
  getMetricOptions: () =>
    api().get<{ data: { metrics: MetricOption[] } }>('/api/v1/mobile/metric/'),

  /** PATCH /api/v1/mobile/metric/user/{userId} */
  updateMetrics: (userId: number, metricIds: number[]) =>
    api().patch(`/api/v1/mobile/metric/user/${userId}`, { data: metricIds }),

  /** GET /api/v1/mobile/account/athlete-zones/user/{userId} */
  getAthleteZones: (userId: number) =>
    getCachedAthleteZones(userId),

  /** PATCH /api/v1/mobile/user/{userId}/calculate-zones */
  calculateUserZones: (userId: number, data: Record<string, unknown>) =>
    api().patch(`/api/v1/mobile/user/${userId}/calculate-zones`, data).then(response => { invalidateAthleteZones(userId); return response; }),

  /** Rename existing zones without changing boundaries, method, or thresholds. */
  updateZoneLabels: (userId: number, zoneType: 'power' | 'heart_rate' | 'speed', zones: Array<{ label: string }>) =>
    api().patch(`/api/v1/mobile/user/${userId}/update-zone-labels`, {
      zone_type: zoneType,
      labels: zones.map((zone, index) => ({ index, label: zone.label })),
    }).then(response => { invalidateAthleteZones(userId); return response; }),

  /** POST /api/v1/mobile/account/create-athlete-zones/{athleteId} */
  createAthleteZones: (athleteId: number, data: Record<string, unknown>) =>
    api().post(`/api/v1/mobile/account/create-athlete-zones/${athleteId}`, data).then(response => { invalidateAthleteZones(athleteId); return response; }),

  /** GET /api/v1/mobile/garmin/auth/{userId}?client=pwa */
  getGarminAuthUrl: (userId: number) =>
    api().get<{ data: string }>(`/api/v1/mobile/garmin/auth/${userId}`, {
      params: { client: 'pwa' },
      headers: { 'X-VO2-Client': 'pwa' },
    }),

  /** DELETE /api/v1/mobile/garmin/account — deregister Garmin for athlete */
  disconnectGarmin: (userId: number) =>
    api().delete('/api/v1/mobile/garmin/account', { data: { user_id: userId } }),

  /** OAuth entry point shared by Wahoo and cloud-provider adapters. */
  getCloudIntegrationAuthUrl: (provider: CloudIntegrationProvider, userId: number) =>
    api().get<{ data: { auth_url: string; expires_in: number } }>(
      provider === 'wahoo'
        ? '/api/v1/mobile/wahoo/auth'
        : `/api/v1/mobile/integrations/${provider}/auth`,
      {
        params: { client: 'pwa', user_id: userId },
        headers: { 'X-VO2-Client': 'pwa' },
      },
    ),

  getCloudIntegrationStatus: (provider: CloudIntegrationProvider, userId: number) =>
    api().get<{ data: CloudIntegrationStatus }>(
      provider === 'wahoo'
        ? '/api/v1/mobile/wahoo/status'
        : `/api/v1/mobile/integrations/${provider}/status`,
      { params: { user_id: userId } },
    ),

  disconnectCloudIntegration: (provider: CloudIntegrationProvider, userId: number) =>
    api().delete(
      provider === 'wahoo'
        ? '/api/v1/mobile/wahoo/account'
        : `/api/v1/mobile/integrations/${provider}/account`,
      { data: { user_id: userId } },
    ),

  /** POST /api/v1/mobile/account/update-profile-image/{userId} */
  updateProfileImage: (userId: number, file: File) => {
    const form = new FormData();
    form.append('image', file);
    return api().post(`/api/v1/mobile/account/update-profile-image/${userId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** POST /api/v1/mobile/account/delete-profile-image/{userId} */
  deleteProfileImage: (userId: number) =>
    api().post(`/api/v1/mobile/account/delete-profile-image/${userId}`, new FormData()),
};
